import type { PropertyValueItem, WorkflowTaskDetail } from "../services/regulationsService";
import type { TaskTableDefinition } from "../types/manualAssessmentWorkflow";
import { resolveTaskTableSelectDisplayLabel } from "./manualAssessmentTaskTable";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/en";

dayjs.extend(customParseFormat);

/** Task-level dueDate from GET workflow task (display: DD-MMM-YYYY with English month). */
export function formatTaskDueDate(value: unknown): string | null {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const strictParsed = dayjs(
    raw,
    [
      "DD/MM/YYYY",
      "DD-MMM-YYYY",
      "DD/MMM/YYYY",
      "DD/MMM/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm:ss",
    ],
    true,
  );
  const parsed = strictParsed.isValid() ? strictParsed : dayjs(raw);

  return parsed.isValid()
    ? parsed.locale("en").format("DD-MMM-YYYY")
    : raw;
}

function isPropertyValueItem(item: unknown): item is PropertyValueItem {
  return item != null && typeof item === "object" && "field" in item;
}

function normalizeHeaderList(raw: unknown): PropertyValueItem[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.filter(isPropertyValueItem);
}

function headersFromTaskTables(raw: unknown): PropertyValueItem[] {
  if (!Array.isArray(raw)) return [];
  for (const table of raw) {
    if (!table || typeof table !== "object") continue;
    const headers = normalizeHeaderList((table as TaskTableDefinition).headers);
    if (headers.length > 0) return headers;
  }
  return [];
}

/**
 * Read task form header fields from GET workflow task.
 * Supports taskPage.headers, task.headers, taskTables[].headers (root or under task).
 */
export function parseTaskTableHeadersFromTaskDetail(
  taskDetail: WorkflowTaskDetail | null | undefined,
): PropertyValueItem[] {
  if (!taskDetail) return [];

  const root = taskDetail as Record<string, unknown>;
  const task = taskDetail.task as Record<string, unknown> | undefined;
  const taskPage = (root.taskPage ?? task?.taskPage) as
    | Record<string, unknown>
    | undefined;

  const candidates: unknown[] = [
    taskPage?.headers,
    task?.headers,
    root.headers,
  ];

  for (const candidate of candidates) {
    const headers = normalizeHeaderList(candidate);
    if (headers.length > 0) return headers;
  }

  const tableSources = [
    taskDetail.taskTables,
    root.taskTables,
    task?.taskTables,
    taskPage?.taskTables,
  ];

  for (const source of tableSources) {
    const headers = headersFromTaskTables(source);
    if (headers.length > 0) return headers;
  }

  return [];
}

export function findTaskTableHeader(
  headers: ReadonlyArray<PropertyValueItem>,
  field: string,
): PropertyValueItem | undefined {
  return headers.find((item) => String(item.field ?? "") === field);
}

export function resolveTaskHeaderLabel(
  header: PropertyValueItem | undefined,
  fallback: string,
): string {
  const label = header?.label ?? header?.originlabel;
  if (typeof label === "string" && label.trim()) return label.trim();
  return fallback;
}

/** True when header exists and fieldValue is non-empty. */
export function hasNonEmptyHeaderFieldValue(
  header: PropertyValueItem | undefined,
): boolean {
  if (!header) return false;
  const value = header.fieldValue;
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as object).length > 0;
  return true;
}

/** Format due date strictly from a header item's fieldValue. */
export function resolveHeaderDueDateDisplay(
  header: PropertyValueItem | undefined,
): string | null {
  if (!hasNonEmptyHeaderFieldValue(header)) return null;
  return formatTaskDueDate(header!.fieldValue);
}

export function buildAvailableOwnersSummary(
  options: ReadonlyArray<{ label: string; value: string | number | boolean }>,
): { count: number; names: string | null } {
  const names = options
    .map((option) => option.label.trim())
    .filter(Boolean);
  return {
    count: names.length,
    names: names.length > 0 ? names.join(", ") : null,
  };
}

export function buildCurrentAssigneeSummary(
  options: ReadonlyArray<{ label: string; value: string | number | boolean }>,
  assigneeId: unknown,
): string | null {
  const label = resolveTaskTableSelectDisplayLabel(options, assigneeId);
  return label.trim() ? label : null;
}

/**
 * Available Owners is the only header specialty: show person-count from the
 * task-assignees option pool (not a raw fieldValue dump).
 */
export function isAvailableOwnersHeader(header: PropertyValueItem): boolean {
  return String(header.field ?? "") === "taskOwners";
}

const HEADER_DATE_PARSE_FORMATS = [
  "DD/MMM/YYYY HH:mm:ss",
  "DD/MM/YYYY HH:mm:ss",
  "DD/MMM/YYYY",
  "DD/MM/YYYY",
  "DD-MMM-YYYY",
] as const;

function isDateLikeHeaderString(raw: string): boolean {
  return dayjs(raw, [...HEADER_DATE_PARSE_FORMATS], true).isValid();
}

export type HeaderSummaryOption = {
  label: string;
  value: string | number | boolean;
};

/**
 * Schema-driven text for a summary-bar header (same PropertyValueItem as DynamicForm).
 * Skips empty fieldValue. Dates / selects resolve by type + options; no field-name whitelist.
 */
export function formatHeaderSummaryDisplayValue(
  header: PropertyValueItem,
  options?: ReadonlyArray<HeaderSummaryOption>,
): string | null {
  if (!hasNonEmptyHeaderFieldValue(header)) return null;

  const value = header.fieldValue;
  const type = String(header.type ?? "").toLowerCase();

  if (type === "date" || (typeof value === "string" && isDateLikeHeaderString(value))) {
    return formatTaskDueDate(value);
  }

  const staticOptions = Array.isArray(header.options)
    ? (header.options as HeaderSummaryOption[])
    : undefined;
  const lookupOptions =
    options && options.length > 0 ? options : staticOptions;

  if (lookupOptions && lookupOptions.length > 0) {
    if (Array.isArray(value)) {
      const labels = value
        .map((entry) =>
          resolveTaskTableSelectDisplayLabel(lookupOptions, entry).trim(),
        )
        .filter(Boolean);
      if (labels.length > 0) return labels.join(", ");
    } else {
      const label = buildCurrentAssigneeSummary(lookupOptions, value);
      if (label) return label;
    }
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  return null;
}

export function hasTaskTableHeaders(
  taskDetail: WorkflowTaskDetail | null | undefined,
): boolean {
  return parseTaskTableHeadersFromTaskDetail(taskDetail).length > 0;
}
